/**
 * AmazonAdvertisingIntegrationPage — OAuth connect + spend sync status + campaign list.
 * Brand name "Amazon Advertising Integration" used once in the title/brand mark;
 * remaining copy uses the neutral "Retail Ads Integration" label.
 */
import { useMemo, useState } from "react";
import {
	useAmazonAdvertisingIntegrationList,
	useAmazonAdvertisingIntegrationCreate,
} from "./hooks";
import type { AmazonAdvertisingIntegrationRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-green-700 bg-white px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";

type Row = AmazonAdvertisingIntegrationRow;

function deriveStatus(rows: Row[]): "not_connected" | "connected" | "syncing" | "error" {
	if (!rows.length) return "not_connected";
	const last = rows[0];
	const status =
		typeof last.status === "string" ? (last.status as string) : "pending";
	if (status === "pending") return "syncing";
	if (status === "error" || status === "failed") return "error";
	return "connected";
}

function formatStatus(s: "not_connected" | "connected" | "syncing" | "error"): {
	label: string;
	classes: string;
} {
	if (s === "connected")
		return { label: "Connected", classes: "bg-green-100 text-green-800" };
	if (s === "syncing")
		return { label: "Sync in progress", classes: "bg-zinc-100 text-zinc-700" };
	if (s === "error")
		return { label: "Sync error", classes: "bg-red-100 text-red-700" };
	return { label: "Not connected", classes: "bg-zinc-100 text-zinc-700" };
}

function groupCampaigns(rows: Row[]): Array<{
	profile_id: string;
	marketplace: string;
	conversion_count: number;
	total_value: number;
	last_seen: number;
}> {
	const buckets = new Map<
		string,
		{
			profile_id: string;
			marketplace: string;
			conversion_count: number;
			total_value: number;
			last_seen: number;
		}
	>();
	for (const r of rows) {
		const profile_id = typeof r.profile_id === "string" ? r.profile_id : "";
		const marketplace =
			typeof r.marketplace === "string" ? r.marketplace : "global";
		const key = `${profile_id}::${marketplace}`;
		const value =
			typeof r.conversion_value === "number" ? r.conversion_value : 0;
		const created_at =
			typeof r.created_at === "number" ? r.created_at : 0;
		const existing = buckets.get(key);
		if (existing) {
			existing.conversion_count += 1;
			existing.total_value += value;
			if (created_at > existing.last_seen) existing.last_seen = created_at;
		} else {
			buckets.set(key, {
				profile_id,
				marketplace,
				conversion_count: 1,
				total_value: value,
				last_seen: created_at,
			});
		}
	}
	return Array.from(buckets.values()).sort((a, b) => b.last_seen - a.last_seen);
}

export function AmazonAdvertisingIntegrationPage() {
	const listQuery = useAmazonAdvertisingIntegrationList();
	const connectMut = useAmazonAdvertisingIntegrationCreate();
	const [connectError, setConnectError] = useState<string | null>(null);

	const rows: Row[] = useMemo(() => {
		const raw = listQuery.data?.items;
		return Array.isArray(raw) ? raw : [];
	}, [listQuery.data]);

	const status = deriveStatus(rows);
	const statusMeta = formatStatus(status);
	const campaigns = useMemo(() => groupCampaigns(rows), [rows]);
	const lastSync = rows.length
		? Math.max(...rows.map((r) => (typeof r.created_at === "number" ? r.created_at : 0)))
		: 0;

	const handleConnect = () => {
		setConnectError(null);
		connectMut.mutate(
			{
				event_id: `oauth-connect-${Date.now()}`,
				profile_id: "pending-oauth",
				conversion_type: "oauth_link",
			},
			{
				onError: (e) => setConnectError(e.message),
			},
		);
	};

	return (
		<main
			data-testid="amazon-advertising-integration-page"
			className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold text-green-700">
					Amazon Advertising Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Connect your Retail Ads Integration account to forward SKAN conversion
					values and campaign spend back to the conversion API.
				</p>
			</header>

			<section
				className={`${CARD} flex flex-col gap-4`}
				data-testid="amazon-advertising-integration-connect-card"
				aria-label="Retail Ads Integration connection"
			>
				<div className="flex items-center justify-between gap-4">
					<div className="flex flex-col gap-1">
						<span className={LABEL}>Retail Ads Integration</span>
						<span className="text-xs text-zinc-500">
							OAuth-linked profile for SKAN + AMC postbacks
						</span>
					</div>
					<span
						className={`${PILL} ${statusMeta.classes}`}
						data-testid="amazon-advertising-integration-status-pill"
						data-status={status}
					>
						{statusMeta.label}
					</span>
				</div>

				<div className="flex flex-wrap items-center gap-3">
					<button
						type="button"
						onClick={handleConnect}
						disabled={connectMut.isPending || status === "connected"}
						className={status === "connected" ? BTN_SECONDARY : BTN_PRIMARY}
						data-testid="amazon-advertising-integration-connect-btn"
					>
						{connectMut.isPending
							? "Connecting."
							: status === "connected"
								? "Reconnect"
								: "Connect Retail Ads Integration"}
					</button>
					<span
						className="text-xs text-zinc-500"
						data-testid="amazon-advertising-integration-sync-info"
					>
						{lastSync > 0
							? `Last spend sync: ${new Date(lastSync * 1000).toISOString()}`
							: "No spend sync yet"}
					</span>
				</div>

				{connectError ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="amazon-advertising-integration-connect-error"
					>
						{connectError}
					</p>
				) : null}
			</section>

			<section
				className={`${CARD} flex flex-col gap-4`}
				data-testid="amazon-advertising-integration-campaigns-card"
				aria-label="Retail Ads Integration campaigns"
			>
				<div className="flex items-center justify-between">
					<span className={LABEL}>Campaigns</span>
					<span className="text-xs text-zinc-500">
						Aggregated from recent postbacks
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="amazon-advertising-integration-loading"
					>
						Loading campaigns.
					</p>
				) : campaigns.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="amazon-advertising-integration-empty"
					>
						No campaigns yet. Connect the Retail Ads Integration to start
						forwarding conversions.
					</p>
				) : (
					<ul
						className="flex flex-col divide-y divide-zinc-200 rounded-[6px] border border-zinc-200"
						data-testid="amazon-advertising-integration-campaigns-list"
					>
						{campaigns.map((c) => (
							<li
								key={`${c.profile_id}-${c.marketplace}`}
								className="flex items-center justify-between gap-3 px-4 py-3"
								data-testid="amazon-advertising-integration-campaign-row"
							>
								<div className="flex flex-col gap-0.5">
									<span className="text-sm font-medium text-zinc-900">
										Profile {c.profile_id || "unknown"}
									</span>
									<span className="text-xs text-zinc-500">
										{c.marketplace} marketplace
									</span>
								</div>
								<div className="flex items-center gap-4 text-right">
									<div className="flex flex-col">
										<span className="text-xs text-zinc-500">Conversions</span>
										<span className="text-sm font-medium text-zinc-900">
											{c.conversion_count}
										</span>
									</div>
									<div className="flex flex-col">
										<span className="text-xs text-zinc-500">Total value</span>
										<span className="text-sm font-medium text-zinc-900">
											{c.total_value.toFixed(2)}
										</span>
									</div>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AmazonAdvertisingIntegrationPage;
